import React, { useState } from "react"

import { NotificationContainer } from "react-notifications"

import dayjs from "src/dayjs"
import ordered from "src/ordered"

import { gql, useMutation, useQuery } from "hooks/urql"

import EditableDisplay from "components/Forms/EditableDisplay"
import LocalTime from "components/LocalTime"
import Pagination from "components/Pagination"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

import EditQuestions from "./EditQuestions"
import ExportButtons from "./ExportButtons"

import "react-notifications/lib/notifications.css"

type SurveyDetailsProps = {
  id: string
}

const SurveyDetails: React.FC<SurveyDetailsProps> = ({ id }) => {
  const [page, setPage] = useState(1)
  const [result] = useQuery({ query: surveyQuery, variables: { id, page } })
  const [, updateSurvey]: any[] = useMutation(updateSurveyMutation)

  return (
    <UrqlLoadingIndicator result={result}>
      {({ data: { survey, surveyQuestions } }) => (
        <div>
          <NotificationContainer />
          <div className="grid grid-cols-2 gap-6">
            <dl className="col-span-1">
              <ExportButtons id={survey.id} />
              <dt>Name</dt>
              <dd>
                <EditableDisplay
                  displayValue={survey.name}
                  save={({ value }) =>
                    updateSurvey({ id, input: { name: value } })
                  }
                />
              </dd>

              <dt>Organization</dt>
              <dd>
                <EditableDisplay
                  displayValue={survey.organization.name}
                  autocomplete
                  isClearable={false}
                  api="/api/admins/organizations/autocomplete_name"
                  save={({ selectedId }) =>
                    updateSurvey({ id, input: { organizationId: selectedId } })
                  }
                />
              </dd>

              <dt>Starts On</dt>
              <dd>
                <EditableDisplay
                  editableValue={dayjs(survey.startsOn).toDate()}
                  displayValue={
                    <LocalTime timestamp={survey.startsOn} omitTime />
                  }
                  type="date"
                  save={({ value }) =>
                    updateSurvey({
                      id,
                      input: { startsOn: value.toDate() },
                    })
                  }
                />
              </dd>

              <dt>Questions</dt>
              <dd>
                <EditQuestions
                  id={id}
                  currentQuestions={survey.questions}
                  allQuestions={surveyQuestions}
                  updateSurvey={updateSurvey}
                />
              </dd>
            </dl>

            <div className="col-span-1">
              <h4>No Response Students</h4>
              <ul className="list-none p-0">
                {survey.noResponseStudents.data.map(student => (
                  <li key={student.id}>
                    <a href={student.showPath}>{student.fullName}</a>
                  </li>
                ))}
              </ul>
              <Pagination
                currentPage={survey.noResponseStudents.currentPage}
                totalPages={survey.noResponseStudents.totalPages}
                changePage={setPage}
              />
            </div>
          </div>

          <div className="my-5">
            <hr />
          </div>

          <div>
            <h3>Assigned Course Clusters</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>School</th>
                  <th>Grades</th>
                  <th>Subject</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {ordered(survey.courseClusters, "name").map(courseCluster => (
                  <tr key={courseCluster.id}>
                    <td>{courseCluster.name}</td>
                    <td>{courseCluster.school?.name}</td>
                    <td>
                      {courseCluster.grades.map(grade => grade.name).join(", ")}
                    </td>
                    <td>{courseCluster.subject.name}</td>
                    <td>
                      <a
                        href={courseCluster.showPath}
                        className="btn btn-sm btn-info"
                      >
                        View
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </UrqlLoadingIndicator>
  )
}

const surveyQuery = gql`
  query Survey($id: ID!, $page: Int) {
    survey(id: $id) {
      id
      name
      startsOn
      organization {
        id
        name
      }
      questions {
        id
        text
      }
      courseClusters {
        id
        name
        showPath
        school {
          id
          name
        }
        grades {
          id
          name
        }
        subject {
          id
          name
        }
      }
      noResponseStudents(page: $page) {
        currentPage
        totalPages
        data {
          id
          fullName
          showPath
        }
      }
    }
    surveyQuestions: allSurveyQuestions {
      id
      text
    }
  }
`

const updateSurveyMutation = gql`
  mutation UpdateSurvey($id: ID!, $input: SurveyInputObject!) {
    updateSurvey(id: $id, input: $input) {
      failures {
        message
      }
      survey {
        id
      }
    }
  }
`

export default SurveyDetails
